// @flow

import * as React from 'react';
import classnames from 'classnames';
import { trackEvent } from 'latitude-analytics';
import type { Analytics } from 'latitude-analytics';
import SvgInline from '@latitude/svg-inline';
import './_search-form.scss';

export type SearchFormDataType = {
  searchText?: string, // search label text when active
  searchAction: string // search form action value
};

type Props = {
  active: boolean,
  analytics: Analytics,
  animateClose?: boolean,
  onRequestClose: () => void,
  searchFormData: SearchFormDataType
};

class SearchForm extends React.Component<Props> {
  formRef: { current: null | HTMLFormElement };

  searchInputContainerRef: null | React.Ref<'div'>;

  searchInputRef: null | React.Ref<'input'>;

  constructor(props: Props) {
    super(props);
    this.formRef = React.createRef();
    this.searchInputContainerRef = React.createRef();
    this.searchInputRef = React.createRef();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClick, false);
    document.addEventListener('keydown', this.handleKeyDown, false);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.active && this.props.active !== prevProps.active) {
      this.searchInputRef.current.focus();
    }
    if (
      this.props.animateClose &&
      this.props.animateClose !== prevProps.animateClose
    ) {
      this.searchInputContainerRef.current.classList.remove('is-active');
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, false);
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  /*
   * Close the popup on Esc key,
   */
  handleKeyDown = (event: KeyboardEvent) => {
    if (!this.props.active) return;
    if (event.keyCode === 27) {
      this.props.onRequestClose();
    }
  };

  /* Closing transition of the search input
   */
  closeSearchInput = () => {
    this.searchInputContainerRef.current.classList.remove('is-active');
  };

  /*
   * Close the popup on click outside of the search form
   */
  handleClick = (ev: MouseEvent) => {
    if (!this.props.active) return;
    const clickTarget = ev.target;
    const wrapperNode = this.formRef && this.formRef.current;
    if (
      wrapperNode &&
      clickTarget instanceof Node &&
      !wrapperNode.contains(clickTarget)
    ) {
      this.props.onRequestClose();
    }
  };

  render() {
    const { active, analytics, onRequestClose, searchFormData } = this.props;
    const searchText = searchFormData && searchFormData.searchText;
    const searchAction = searchFormData && searchFormData.searchAction;

    return (
      <form
        ref={this.formRef}
        key="abc"
        className={classnames('search', {
          'is-active': active
        })}
        method="get"
        action={searchAction}
      >
        <div className="search__label">{searchText || 'Search'}</div>
        <button
          type="button"
          className="search__close search__close--mobile"
          onClick={onRequestClose}
        >
          <SvgInline
            className="search__close-icon"
            name="cross"
            ariaLabel="Close search"
          />
        </button>

        <div
          ref={this.searchInputContainerRef}
          className={classnames('search__input-container', {
            'is-active': active
          })}
        >
          {/* eslint-disable jsx-a11y/no-autofocus */}
          <input
            ref={this.searchInputRef}
            type="text"
            autoFocus
            className="search__input"
            name="q"
          />

          <button
            type="button"
            className="search__submit"
            onClick={() => {
              trackEvent(analytics, {
                category: 'search',
                action: 'interaction',
                label: 'search',
                location: 'header'
              });
              this.formRef.current.submit();
            }}
          >
            <SvgInline
              className="search__submit-icon"
              aria-label="Search"
              name="search"
            />
          </button>
        </div>

        <button
          type="button"
          className="search__close search__close--desktop"
          onClick={onRequestClose}
        >
          <SvgInline
            className="search__close-icon"
            name="cross"
            ariaLabel="Close search"
          />
        </button>
      </form>
    );
  }
}

export default SearchForm;
